import axios, { type AxiosError } from "axios";

export const apiClient = axios.create({
  baseURL: "",
  withCredentials: true, // send Django session cookie
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    // NOTE: Content-Type is NOT set here. It is added per-request in the
    // interceptor below so that FormData uploads can let the browser set the
    // multipart boundary automatically (setting it here would break file uploads).
  },
});

// Grab CSRF token from cookie and add to mutating requests. Exported so raw
// fetch() callers (e.g. SSE streams that can't use axios) send the same header.
export function getCsrfToken(): string {
  const name = "resumeai_csrftoken";
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [key, value] = cookie.trim().split("=");
    if (key === name) return decodeURIComponent(value ?? "");
  }
  return "";
}

apiClient.interceptors.request.use((config) => {
  if (
    config.method &&
    ["post", "put", "patch", "delete"].includes(config.method)
  ) {
    config.headers["X-CSRFToken"] = getCsrfToken();
  }
  // Set JSON content type only when the body is NOT FormData.
  // For FormData the browser sets multipart/form-data with the correct boundary.
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

// Redirect to login on 401, preserving the current path as ?next=
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      error.response?.status === 401 &&
      !window.location.pathname.startsWith("/login")
    ) {
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/login?next=${next}`;
    }
    return Promise.reject(error);
  },
);

export default apiClient;
